import { useState, useEffect } from 'react'
import { Plus, Upload, Eye, Edit2, Trash2, ArrowUp, ArrowDown, X } from 'lucide-react'
import { adminAPI } from '../services/api'

const Banners = () => {
  const [banners, setBanners] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedBanner, setSelectedBanner] = useState(null)
  const [modalMode, setModalMode] = useState('create') // 'create', 'edit'
  const [formData, setFormData] = useState({
    title: '',
    imageUrl: '',
    linkUrl: '',
    orderIndex: 0,
    isActive: true
  })
  const [imageFile, setImageFile] = useState(null)
  const [uploadMode, setUploadMode] = useState('file') // 'file' или 'url'
  const [imagePreview, setImagePreview] = useState(null)

  useEffect(() => {
    fetchBanners()
  }, [])

  // Блокируем скролл фона когда открыта модалка
  useEffect(() => {
    if (showModal || showDeleteModal) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    
    // Очищаем при размонтировании
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [showModal, showDeleteModal])

  const fetchBanners = async () => {
    try {
      setLoading(true)
      const response = await adminAPI.getBanners()
      
      if (response.data.success) {
        // Маппим поля API к ожидаемому формату
        const mappedBanners = (response.data.banners || []).map(banner => ({
          id: banner.id,
          title: banner.title,
          imageUrl: banner.imageUrl || banner.image_url,
          linkUrl: banner.linkUrl || banner.link_url,
          orderIndex: banner.orderIndex || banner.order_index,
          isActive: banner.isActive || banner.is_active,
          createdAt: banner.createdAt || banner.created_at,
          createdBy: banner.createdBy || banner.created_by,
          createdByName: banner.createdByName || banner.created_by_name,
          updatedAt: banner.updatedAt || banner.updated_at
        }))
        
        // Сортуємо баннери за orderIndex
        const sortedBanners = mappedBanners.sort((a, b) => a.orderIndex - b.orderIndex)
        setBanners(sortedBanners)
      }
    } catch (error) {
      console.error('Error fetching banners:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateBanner = () => {
    setSelectedBanner(null)
    setModalMode('create')
    setFormData({
      title: '',
      imageUrl: '',
      linkUrl: '',
      orderIndex: banners.length,
      isActive: true
    })
    setImageFile(null)
    setUploadMode('file')
    setImagePreview(null)
    setShowModal(true)
  }

  const handleEditBanner = (banner) => {
    setSelectedBanner(banner)
    setModalMode('edit')
    setFormData({
      title: banner.title,
      imageUrl: banner.imageUrl,
      linkUrl: banner.linkUrl || '',
      orderIndex: banner.orderIndex,
      isActive: banner.isActive
    })
    setImageFile(null)
    setImagePreview(null)
    setUploadMode('url') // По умолчанию показываем URL режим
    setShowModal(true)
  }

  const handleDeleteBanner = (banner) => {
    setSelectedBanner(banner)
    setShowDeleteModal(true)
  }

  const handleModalClose = () => {
    setShowModal(false)
    setSelectedBanner(null)
    setImageFile(null)
    setImagePreview(null)
  }

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false)
    setSelectedBanner(null)
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setImageFile(file)
      
      // Создаем предварительный просмотр
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Валидация для режима загрузки файла
    if (modalMode === 'create' && uploadMode === 'file' && !imageFile) {
      alert('Пожалуйста, выберите изображение для загрузки')
      return
    }
    
    // Валидация размера файла
    if (imageFile && imageFile.size > 5 * 1024 * 1024) {
      alert('Размер файла не должен превышать 5MB')
      return
    }
    
    // При редактировании в режиме "Заменить фото" файл не обязателен
    if (modalMode === 'edit' && uploadMode === 'file' && !imageFile) {
      // Если файл не выбран, переключаемся на обычное редактирование
      uploadMode = 'url'
    }
    
    setLoading(true)

    try {
      if (modalMode === 'create') {
        if (uploadMode === 'file' && imageFile) {
          // Создание с загрузкой файла
          const formDataToSend = new FormData()
          formDataToSend.append('image', imageFile)
          formDataToSend.append('title', formData.title)
          formDataToSend.append('linkUrl', formData.linkUrl)
          formDataToSend.append('orderIndex', formData.orderIndex)
          
          await adminAPI.createBannerWithFile(formDataToSend)
        } else {
          // Создание с URL
        await adminAPI.createBanner(formData)
        }
      } else {
        // Редактирование
        if (uploadMode === 'file' && imageFile) {
          // Редактирование с заменой фото
          const formDataToSend = new FormData()
          formDataToSend.append('image', imageFile)
          formDataToSend.append('title', formData.title)
          formDataToSend.append('linkUrl', formData.linkUrl)
          formDataToSend.append('orderIndex', formData.orderIndex)
          formDataToSend.append('isActive', formData.isActive)
          
          await adminAPI.updateBannerWithFile(selectedBanner.id, formDataToSend)
        } else {
          // Обычное редактирование без замены фото (все поля опциональны)
          const updateData = {}
          if (formData.title !== selectedBanner.title) updateData.title = formData.title
          if (formData.imageUrl !== selectedBanner.imageUrl) updateData.imageUrl = formData.imageUrl
          if (formData.linkUrl !== selectedBanner.linkUrl) updateData.linkUrl = formData.linkUrl
          if (formData.orderIndex !== selectedBanner.orderIndex) updateData.orderIndex = formData.orderIndex
          if (formData.isActive !== selectedBanner.isActive) updateData.isActive = formData.isActive
          
          await adminAPI.updateBanner(selectedBanner.id, updateData)
        }
      }

      fetchBanners()
      handleModalClose()
    } catch (error) {
      console.error('Error saving banner:', error)
      alert('Ошибка при сохранении баннера: ' + (error.response?.data?.error || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteConfirm = async () => {
    try {
      await adminAPI.deleteBanner(selectedBanner.id)
      fetchBanners()
      handleDeleteModalClose()
    } catch (error) {
      console.error('Error deleting banner:', error)
    }
  }

  const handleReorder = async (bannerId, direction) => {
    const bannerIndex = banners.findIndex(b => b.id === bannerId)
    if (
      (direction === 'up' && bannerIndex === 0) ||
      (direction === 'down' && bannerIndex === banners.length - 1)
    ) {
      return
    }

    const newBanners = [...banners]
    let targetIndex
    if (direction === 'up') {
      targetIndex = bannerIndex - 1
    } else {
      targetIndex = bannerIndex + 1
    }
    
    // Міняємо місцями
    [newBanners[bannerIndex], newBanners[targetIndex]] = [newBanners[targetIndex], newBanners[bannerIndex]]
    
    // Оновлюємо orderIndex
    const reorderedIds = newBanners.map(banner => banner.id)
    
    try {
      await adminAPI.reorderBanners(reorderedIds)
      setBanners(newBanners)
    } catch (error) {
      console.error('Error reordering banners:', error)
    }
  }

  const getStatusBadge = (isActive) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full"
    return isActive 
      ? `${baseClasses} bg-green-100 text-green-800`
      : `${baseClasses} bg-gray-100 text-gray-800`
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('uk-UA')
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Баннери</h1>
          <p className="text-gray-600">Управління рекламними баннерами</p>
        </div>
        <button
          onClick={handleCreateBanner}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Додати баннер</span>
        </button>
      </div>

      {/* Список баннерів */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Всі баннери</h3>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {banners.map((banner, index) => (
              <div key={banner.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => handleReorder(banner.id, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Перемістити вгору"
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        onClick={() => handleReorder(banner.id, 'down')}
                        disabled={index === banners.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Перемістити вниз"
                      >
                        <ArrowDown size={16} />
                      </button>
                    </div>
                    
                    <div className="shadow-2xl w-60 h-28 bg-gray-100 rounded-lg overflow-hidden">
                      <img 
                        src={banner.imageUrl} 
                        alt={banner.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-gray-900">{banner.title}</h4>
                        <span className={getStatusBadge(banner.isActive)}>
                          {banner.isActive ? 'Активний' : 'Неактивний'}
                        </span>
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          Порядок: {banner.orderIndex}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        Посилання: {banner.linkUrl || 'Не вказано'}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>ID: {banner.id}</span>
                        <span>Створено: {formatDate(banner.createdAt)}</span>
                        {banner.createdByName && (
                          <span>Автор: {banner.createdByName}</span>
                        )}
                        {banner.updatedAt && banner.updatedAt !== banner.createdAt && (
                          <span>Оновлено: {formatDate(banner.updatedAt)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => window.open(banner.imageUrl, '_blank')}
                      className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                      title="Переглянути зображення"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => handleEditBanner(banner)}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Редагувати"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteBanner(banner)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Видалити"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {banners.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">Баннери не знайдені</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Модальне вікно створення/редагування */}
      {showModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">
                {modalMode === 'create' ? 'Додати баннер' : 'Редагувати баннер'}
              </h2>
              <button
                onClick={handleModalClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Назва баннера *
                </label>
                <input
                  type="text"
                  required
                  className="input"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Введіть назву баннера"
                />
              </div>

              {/* Переключатель режима загрузки */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {modalMode === 'create' ? 'Способ загрузки изображения' : 'Редактирование изображения'}
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="uploadMode"
                      value="file"
                      checked={uploadMode === 'file'}
                      onChange={(e) => setUploadMode(e.target.value)}
                      className="mr-2"
                    />
                    {modalMode === 'create' ? 'Загрузить файл' : 'Заменить фото'}
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="uploadMode"
                      value="url"
                      checked={uploadMode === 'url'}
                      onChange={(e) => setUploadMode(e.target.value)}
                      className="mr-2"
                    />
                    {modalMode === 'create' ? 'Указать URL' : 'Изменить URL'}
                  </label>
                </div>
              </div>

              {/* Поле для загрузки файла */}
              {uploadMode === 'file' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Изображение {modalMode === 'create' ? '*' : '(опционально)'}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    required={modalMode === 'create'}
                    className="input"
                    onChange={handleFileChange}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Поддерживаемые форматы: JPG, PNG, GIF, WebP. Максимальный размер: 5MB
                    {modalMode === 'edit' && ' Если файл не выбран, изображение останется прежним.'}
                  </p>
                  {imageFile && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-600 mb-2">Выбранный файл: {imageFile.name}</p>
                      <p className="text-xs text-gray-500 mb-2">Размер: {(imageFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      {imagePreview && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-600 mb-1">Предварительный просмотр:</p>
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="w-32 h-20 object-cover rounded border"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Поле для URL */}
              {uploadMode === 'url' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL зображення *
                </label>
                <input
                  type="url"
                  required
                  className="input"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                  placeholder="https://example.com/image.jpg"
                />
                  {modalMode === 'edit' && formData.imageUrl && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-600 mb-2">Текущее изображение:</p>
                      <img 
                        src={formData.imageUrl} 
                        alt="Current banner" 
                        className="w-32 h-20 object-cover rounded border"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                    </div>
                  )}
              </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL посилання
                </label>
                <input
                  type="url"
                  className="input"
                  value={formData.linkUrl}
                  onChange={(e) => setFormData({...formData, linkUrl: e.target.value})}
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Порядок відображення
                </label>
                <input
                  type="number"
                  min="0"
                  className="input"
                  value={formData.orderIndex}
                  onChange={(e) => setFormData({...formData, orderIndex: parseInt(e.target.value) || 0})}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Менше число = вище в списку
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  className="h-4 w-4 text-gray-900 focus:ring-gray-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Баннер активний
                </label>
              </div>
              </div>

              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="btn btn-secondary"
                >
                  Скасувати
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Збереження...' : modalMode === 'create' ? 'Створити' : 'Зберегти'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Модальне вікно підтвердження видалення */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Видалити баннер?
              </h3>
              <p className="text-gray-600 mb-6">
                Ви впевнені, що хочете видалити баннер "{selectedBanner?.title}"? 
                Цю дію неможливо скасувати.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteModalClose}
                  className="btn btn-secondary"
                >
                  Скасувати
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="btn btn-danger"
                >
                  Видалити
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Banners

